import { Component } from '@theme/component';

/**
 * A floating "back to top" button that appears once the user has scrolled
 * past a threshold and smoothly scrolls the page back to the top on click.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} button - The clickable button element.
 *
 * @extends {Component<Refs>}
 */
class BackToTop extends Component {
  requiredRefs = ['button'];

  /**
   * The scroll distance (in pixels) after which the button becomes visible.
   * @type {number}
   */
  #threshold = 400;

  /** @type {boolean} */
  #visible = false;

  /** @type {boolean} */
  #ticking = false;

  connectedCallback() {
    super.connectedCallback();

    const threshold = Number(this.getAttribute('threshold'));
    if (!Number.isNaN(threshold) && threshold > 0) {
      this.#threshold = threshold;
    }

    window.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('scroll', this.#onScroll);
  }

  #onScroll = () => {
    if (this.#ticking) return;
    this.#ticking = true;
    requestAnimationFrame(() => {
      this.#update();
      this.#ticking = false;
    });
  };

  #update() {
    const visible = window.scrollY > this.#threshold;
    if (visible === this.#visible) return;
    this.#visible = visible;
    this.toggleAttribute('visible', visible);
  }

  /**
   * Scrolls the page back to the top.
   */
  scrollToTop = () => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
  };
}

if (!customElements.get('back-to-top')) {
  customElements.define('back-to-top', BackToTop);
}
